import { Doc } from '@dadohr/shared/domainTypes/document';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { PaletteColor, ThemeOverrides } from '@dadohr/shared/domainTypes/theme';
import { FS } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import {
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip
} from '@material-ui/core';
import React, { useEffect, useMemo, useState } from 'react';
import { ButtonWithPromise } from '../../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../../components/DialogActionsWithSlots';
import { OrgLogo } from '../../../../../../../components/OrgLogo';
import { useDialogState } from '../../../../../../../hooks/useDialogState';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { Section } from '../../../../../../../layout/Section';
import { HuggingSection } from '../HuggingSection';
import { isHexColor } from '../../../../../../../services/color';
import { store } from '../../../../../../../services/firebase';
import { printHourWithTimezone } from '../../../../../../../services/time';
import { AppThemeProviderWithOptionalCustomizations } from '../../../../../../../themes/provider';

type Props = {
  org: Doc<IOrganization>;
};

const Row = styled('div')`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* Keeps a comfortable gap between label and value when the card hugs its
     content (fitContent) — otherwise the widest row's label and value touch. */
  gap: ${(p) => p.theme.spacing(6)}px;

  margin-bottom: ${(p) => p.theme.spacing()}px;

  > :first-of-type {
    font-weight: ${(p) => p.theme.typography.fontWeightBold};
  }

  > ::nth-child(2) {
    display: flex;
    justify-content: flex-end;
    align-items: center;
  }
`;

const NotSupportedDialog = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogContent>Not supported yet.</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const StringUpdateDialog = ({
  open,
  onClose,
  value,
  onChange
}: {
  open: boolean;
  onClose: () => void;
  value: string;
  onChange: (nextValue: string) => Promise<unknown>;
}) => {
  const [x, setX] = useState(value);
  useEffect(() => {
    setX(value);
  }, [value]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <TextField
          fullWidth={true}
          value={x}
          variant="outlined"
          onChange={(ev) => setX(ev.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <ButtonWithPromise
          disabled={value === x}
          pending="Updating..."
          variant="contained"
          color="secondary"
          onClick={() => onChange(x).then(() => onClose())}
        >
          Update
        </ButtonWithPromise>
      </DialogActions>
    </Dialog>
  );
};

const NumberUpdateDialog = ({
  open,
  onClose,
  value,
  onChange,
  min,
  max
}: {
  open: boolean;
  onClose: () => void;
  value: number;
  onChange: (nextValue: number) => Promise<unknown>;
  min?: number;
  max?: number;
}) => {
  const [x, setX] = useState(value);
  useEffect(() => {
    setX(value);
  }, [value]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <TextField
          type="number"
          inputProps={{ min, max }}
          fullWidth={true}
          value={x}
          variant="outlined"
          onChange={(ev) => setX(parseInt(ev.target.value))}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <ButtonWithPromise
          disabled={value === x}
          pending="Updating..."
          variant="contained"
          color="secondary"
          onClick={() => onChange(x).then(() => onClose())}
        >
          Update
        </ButtonWithPromise>
      </DialogActions>
    </Dialog>
  );
};

const EditableRow = <T extends unknown>({
  label,
  value,
  valueRendered,
  onChange
}: {
  label: React.ReactNode;
  valueRendered?: React.ReactNode;
  value: T;
  onChange: (nextValue: T) => Promise<unknown>;
}) => {
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  console.log(label, typeof value);
  return (
    <>
      <Tooltip title="Click to edit">
        <Row role="button" onClick={openDialog}>
          <div>{label}</div>
          <div>{valueRendered}</div>
        </Row>
      </Tooltip>
      {typeof value === 'string' && (
        <StringUpdateDialog
          open={dialogOpen}
          onClose={closeDialog}
          value={value}
          onChange={onChange as any}
        />
      )}
      {typeof value === 'number' && (
        <NumberUpdateDialog
          open={dialogOpen}
          onClose={closeDialog}
          value={value}
          onChange={onChange as any}
        />
      )}
      {typeof value !== 'string' && typeof value !== 'number' && (
        <NotSupportedDialog open={dialogOpen} onClose={closeDialog} />
      )}
    </>
  );
};

const hasAllColors = (x: PaletteColor) => {
  return (
    x.main &&
    isHexColor(x.main) &&
    x.light &&
    isHexColor(x.light) &&
    x.dark &&
    isHexColor(x.dark) &&
    x.contrastText &&
    isHexColor(x.contrastText)
  );
};

const validateTheme = (t: ThemeOverrides) => {
  let isValid = true;
  try {
    if (t.colors) {
      const { primary, secondary, text } = t.colors;
      if (primary && !hasAllColors(primary)) {
        isValid = false;
      }
      if (secondary && !hasAllColors(secondary)) {
        isValid = false;
      }
      if (
        text &&
        !(
          text.primary &&
          isHexColor(text.primary) &&
          text.secondary &&
          isHexColor(text.secondary)
        )
      ) {
        isValid = false;
      }
    } else {
      isValid = false;
    }
  } catch {
    isValid = false;
  }

  return isValid ? null : 'Invalid theme';
};

const ThemeEditDialog = ({
  open,
  onClose,
  value,
  onChange
}: {
  open: boolean;
  onClose: () => void;
  value: ThemeOverrides | null;
  onChange: (nextValue: ThemeOverrides | null) => void;
}) => {
  const [v, setV] = useState(value ? JSON.stringify(value, null, 2) : '');
  useEffect(() => {
    setV(value ? JSON.stringify(value, null, 2) : '');
  }, [value]);
  const parsedV = useMemo(() => {
    try {
      return JSON.parse(v);
    } catch {
      return 'INVALID_JSON';
    }
  }, [v]);
  const validationError =
    parsedV === 'INVALID_JSON' ? 'Invalid JSON' : validateTheme(parsedV);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Edit Theme</DialogTitle>
      <DialogContent>
        <FlexContainer fullWidth>
          <TextField
            multiline
            fullWidth
            rows={30}
            value={v}
            onChange={(ev) => setV(ev.target.value)}
            error={!!validationError}
            helperText={validationError || ''}
          />
        </FlexContainer>
      </DialogContent>
      <DialogActionsWithSlots
        left={
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              onChange(null);
              onClose();
            }}
          >
            Remove
          </Button>
        }
        right={
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!!validationError}
              onClick={() => {
                onChange(parsedV);
                onClose();
              }}
            >
              Save
            </Button>
          </>
        }
      />
    </Dialog>
  );
};

const ThemeRow = ({
  value,
  onChange
}: {
  value: ThemeOverrides | null;
  onChange: (nextValue: ThemeOverrides | null) => void;
}) => {
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  return (
    <>
      <Row role="button" onClick={openDialog}>
        <div>Theme Overrides</div>
        {!value && <div>-</div>}
        {value && (
          <AppThemeProviderWithOptionalCustomizations theme={value}>
            <FlexContainer>
              {value?.colors?.primary && (
                <Button variant="contained" color="primary" size="small">
                  Primary
                </Button>
              )}
              {value?.colors?.secondary && (
                <Button variant="contained" color="secondary" size="small">
                  Secondary
                </Button>
              )}
              {value?.colors?.text && <div>Normal text</div>}
            </FlexContainer>
          </AppThemeProviderWithOptionalCustomizations>
        )}
      </Row>
      <ThemeEditDialog
        open={dialogOpen}
        onClose={closeDialog}
        value={value}
        onChange={onChange}
      />
    </>
  );
};

export const General: React.FC<Props> = ({ org }) => {
  const updateOrg = (update: Partial<IOrganization>) =>
    store().collection(FS.orgs).doc(org.id).update(update);
  return (
    <Section>
      <HuggingSection
        header={
          <CanvasBar>
            <div>General</div>
          </CanvasBar>
        }
      >
      <Card>
        <EditableRow
          label="Name"
          value={org.data.name}
          valueRendered={org.data.name}
          onChange={(name) => updateOrg({ name })}
        />

        <EditableRow
          label="Domain"
          value={org.data.domain}
          valueRendered={org.data.domain}
          onChange={(domain) => updateOrg({ domain })}
        />

        <EditableRow
          label="TZ"
          value={org.data.tz}
          valueRendered={org.data.tz}
          onChange={(tz) => updateOrg({ tz })}
        />

        <EditableRow
          label="Default Experience Start Hour"
          value={org.data.defaultExperienceStartHour}
          valueRendered={printHourWithTimezone(
            org.data.defaultExperienceStartHour,
            org.data.tz
          )}
          onChange={(defaultExperienceStartHour) =>
            updateOrg({ defaultExperienceStartHour })
          }
        />

        <EditableRow
          label="Logo"
          value={org.data.logoUrl}
          valueRendered={org.data.logoUrl ? <OrgLogo d={org.data} /> : ''}
          onChange={(logoUrl) => updateOrg({ logoUrl })}
        />

        <ThemeRow
          value={org.data.experienceTheming}
          onChange={(nextExperienceTheming) =>
            updateOrg({ experienceTheming: nextExperienceTheming })
          }
        />
      </Card>
      </HuggingSection>
    </Section>
  );
};
